// PDF Edit (0.50): os arquivos da aba, o que precisa ver a página (imagens, compressão, efeitos), a IA e onde guardar.
import { zipSync } from 'fflate';
import { getDoc, setDoc } from '../store/store';
import type { DestinoScan, PdfArq } from '../store/types';
import { uid } from './date';
import { t } from './i18n';
import { paraB64 } from './cofre';
import { perguntar, idiomaResposta } from './ia';
import { salvarECompartilhar } from './arquivoSalvar';
import { guardarBytes, nomeSeguro } from './guardar';
import { aparelhoScan, temTratamento } from './scanArquivos';
import { apagarArquivoPdf, escolherPdfs, existeArquivo, gravarArquivo, lerArquivo } from './pdfArquivos';
import { esquecerRender, renderizar, temRenderizador, textoDasPaginas } from './pdfRender';
import { carregar, comTextoInvisivel, contarPaginas, paginasComoImagem, regravar, tamanhosDasPaginas } from './pdfEdit';
import { codificar, decodificar, diminuir, endireitar, filtrar, type Filtro, type Img } from './scanImagem';
import { montarDocx } from './docx';

export type Saida = { nome: string; mime: string; bytes: Uint8Array; ext: string };
export const MIME_PDF = 'application/pdf';

// ─── arquivos da aba ───
const mudar = (fn: (l: PdfArq[]) => PdfArq[]) => setDoc('pdfedit', d => ({ ...d, arquivos: fn(d.arquivos ?? []) }));
export const arqPorId = (id: string) => (getDoc('pdfedit').arquivos ?? []).find(a => a.id === id);
export const destinoPdf = (): DestinoScan => getDoc('pdfedit').guardar ?? 'aparelho';
export const mudarDestinoPdf = (g: DestinoScan) => setDoc('pdfedit', d => ({ ...d, guardar: g }));
export const guardarSozinho = () => getDoc('pdfedit').auto !== false;
export const mudarAuto = (v: boolean) => setDoc('pdfedit', d => ({ ...d, auto: v }));

const ehPdf = (b: Uint8Array) => b.length > 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46;   // %PDF
export const semExt = (n: string) => n.replace(/\.[a-z0-9]{2,5}$/i, '');

export async function adicionarArquivo(nome: string, bytes: Uint8Array, de?: string): Promise<PdfArq> {
  const id = uid();
  const uri = await gravarArquivo(id, bytes);
  let paginas = 0; try { paginas = await contarPaginas(bytes); } catch { /* conta depois */ }
  const a: PdfArq = { id, nome: semExt(nome).slice(0, 120) || t('Documento'), paginas, bytes: bytes.length, criado: new Date().toISOString(), de,
    local: { aparelho: await aparelhoScan(), uri } };
  mudar(l => [a, ...l]);
  return a;
}

/** Abre PDFs do celular, do Drive ou do OneDrive (o seletor do sistema mostra todos). */
export async function importar(multiplo = true): Promise<PdfArq[]> {
  const es = await escolherPdfs(multiplo);
  const out: PdfArq[] = [];
  for (const e of es) {
    const b = await lerArquivo(e.uri);
    if (!ehPdf(b)) throw new Error(t('"{nome}" não é um PDF.', { nome: e.nome }));
    try { await carregar(b); } catch { throw new Error(t('Não consegui abrir "{nome}". Tente a ferramenta Reparar PDF.', { nome: e.nome })); }
    if (e.atalho) {
      let paginas = 0; try { paginas = await contarPaginas(b); } catch { /* conta depois */ }
      const x: PdfArq = { id: uid(), nome: semExt(e.nome).slice(0, 120) || t('Documento'), paginas, bytes: b.length, criado: new Date().toISOString(), de: 'importado',
        local: { aparelho: await aparelhoScan(), uri: e.uri, atalho: true } };
      mudar(l => [x, ...l]);
      out.push(x);
    } else out.push(await adicionarArquivo(e.nome, b, 'importado'));
  }
  return out;
}

export async function estaAquiArq(a: PdfArq) { return !!a.local && a.local.aparelho === (await aparelhoScan()) && existeArquivo(a.local.uri); }
export async function bytesDe(a: PdfArq): Promise<Uint8Array> {
  if (await estaAquiArq(a)) return lerArquivo(a.local!.uri);
  throw new Error(t('Este PDF está no dispositivo onde foi aberto. Abra por lá, ou abra de novo a cópia guardada no Drive ou no OneDrive.'));
}
/** Cópia do PDF (0.52): um arquivo novo, separado do original. */
export async function duplicarArq(a: PdfArq) { return adicionarArquivo(t('{titulo} (cópia)', { titulo: semExt(a.nome) }), await bytesDe(a), a.id); }
export const arquivarArq = (id: string, v: boolean) => mudar(l => l.map(a => (a.id === id ? { ...a, arquivado: v } : a)));
export const renomearArq = (id: string, nome: string) => mudar(l => l.map(a => (a.id === id ? { ...a, nome: nome.trim() || a.nome } : a)));
export function apagarArq(id: string) {
  const a = arqPorId(id);
  mudar(l => l.filter(x => x.id !== id));
  // atalho (o PDF original do usuário) nunca é apagado: sai só da lista
  if (a?.local) { esquecerRender(a.local.uri); if (!a.local.atalho) aparelhoScan().then(ap => { if (a.local!.aparelho === ap) apagarArquivoPdf(a.local!.uri); }).catch(() => {}); }
}

export async function compartilharArq(a: PdfArq) { await salvarECompartilhar(await bytesDe(a), nomeSeguro(a.nome, '.pdf'), MIME_PDF, false); }
export async function guardarArq(a: PdfArq, onde = destinoPdf(), semPerguntar = false) {
  const r = await guardarBytes(nomeSeguro(a.nome, '.pdf'), MIME_PDF, await bytesDe(a), onde, semPerguntar);
  if (!r) return null;
  const { msg, ...salvo } = r;
  mudar(l => l.map(x => (x.id === a.id ? { ...x, salvo } : x)));
  return msg;
}
export const guardarSaida = (s: Saida, onde = destinoPdf(), semPerguntar = false) => guardarBytes(nomeSeguro(s.nome, s.ext), s.mime, s.bytes, onde, semPerguntar);
export const compartilharSaida = (s: Saida) => salvarECompartilhar(s.bytes, nomeSeguro(s.nome, s.ext), s.mime);

// ─── páginas como imagem ───
export { temRenderizador };
export const precisaApkNovo = () => t('Esta ferramenta precisa ver as páginas do PDF, o que chega com o app novo (versão 0.50). Use o navegador ou atualize o My Planner pela loja.');
async function bytesDaImagem(uri: string) { return lerArquivo(uri); }
const respirar = () => new Promise(r => setTimeout(r, 20));

/** Imagem de cada página (JPEG). tratar: mexe nos pixels antes (filtro, efeito de scanner). */
export async function imagensDasPaginas(a: PdfArq, o: { maxLado: number; qualidade: number; tratar?: (img: Img, i: number) => Img }, aoAvancar?: (i: number, n: number) => void) {
  if (!temRenderizador()) throw new Error(precisaApkNovo());
  if (!(await estaAquiArq(a))) await bytesDe(a);
  const n = a.paginas || (await contarPaginas(await bytesDe(a)));
  const out: Uint8Array[] = [];
  for (let i = 0; i < n; i++) {
    aoAvancar?.(i, n);
    await respirar();
    const r = await renderizar(a.local!.uri, i, o.maxLado, o.tratar ? 92 : o.qualidade);
    let jpg = await bytesDaImagem(r.uri);
    if (o.tratar) jpg = codificar(o.tratar(decodificar(jpg), i), o.qualidade);
    out.push(jpg);
  }
  return out;
}

/** Refaz o PDF com as páginas em imagem (comprimir, efeito de scanner, ajuste visual, tarja de verdade). */
export async function rasterizar(a: PdfArq, o: { maxLado: number; qualidade: number; tratar?: (img: Img, i: number) => Img; textos?: string[] }, aoAvancar?: (i: number, n: number) => void) {
  const tam = await tamanhosDasPaginas(await bytesDe(a));
  const jpgs = await imagensDasPaginas(a, o, aoAvancar);
  return paginasComoImagem(jpgs.map((jpg, i) => ({ jpg, tamanho: tam[i], texto: o.textos?.[i] })));
}

export async function paraImagens(a: PdfArq, qualidade: 'alta' | 'media', aoAvancar?: (i: number, n: number) => void): Promise<Saida> {
  const jpgs = await imagensDasPaginas(a, { maxLado: qualidade === 'alta' ? 2200 : 1400, qualidade: qualidade === 'alta' ? 88 : 75 }, aoAvancar);
  if (jpgs.length === 1) return { nome: a.nome, mime: 'image/jpeg', bytes: jpgs[0], ext: '.jpg' };
  const z: Record<string, Uint8Array> = {};
  jpgs.forEach((j, i) => { z[`${nomeSeguro(a.nome, '')}-${String(i + 1).padStart(3, '0')}.jpg`] = j; });
  return { nome: a.nome + ' (imagens)', mime: 'application/zip', bytes: zipSync(z, { level: 0 }), ext: '.zip' };
}

export type Nivel = 'sem-perda' | 'recomendada' | 'maxima';
export async function comprimir(a: PdfArq, nivel: Nivel, aoAvancar?: (i: number, n: number) => void) {
  const original = await bytesDe(a);
  if (nivel === 'sem-perda') return regravar(original);
  const o = nivel === 'recomendada' ? { maxLado: 1500, qualidade: 62 } : { maxLado: 1100, qualidade: 45 };
  return rasterizar(a, o, aoAvancar);
}

/** Efeito de scanner: cinza, levemente torto, com o ruído de uma cópia escaneada. */
export function efeitoScanner(img: Img, i: number): Img {
  const s = ((i * 7919) % 11) / 11 - 0.5;   // tortinho diferente em cada página, sempre igual para a mesma página
  const d = 0.008 + Math.abs(s) * 0.006;
  const tortinha = endireitar(img, [{ x: 0, y: d * Math.sign(s || 1) }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 - d * Math.sign(s || 1) }], Math.max(img.w, img.h));
  const g = filtrar(tortinha, 'cinza');
  let semente = 1234 + i;
  const aleatorio = () => { semente = (semente * 16807) % 2147483647; return semente / 2147483647; };
  for (let p = 0; p < g.d.length; p += 4) {
    const r = (aleatorio() - 0.5) * 18;
    const v = Math.max(0, Math.min(255, g.d[p] * 0.96 + r + 4));
    g.d[p] = v; g.d[p + 1] = v; g.d[p + 2] = v;
  }
  return g;
}
export const ajusteVisual = (f: Filtro) => (img: Img) => filtrar(img, f);

// ─── texto e IA ───
/** Texto de cada página: o que já vem no PDF (navegador) ou a IA lendo a imagem da página. */
export async function textoPorPagina(a: PdfArq, aoAvancar?: (i: number, n: number) => void): Promise<string[]> {
  if (!(await estaAquiArq(a))) await bytesDe(a);
  try {
    const embutido = await textoDasPaginas(a.local!.uri);
    if (embutido && embutido.join('').replace(/\s/g, '').length > 30 * embutido.length) return embutido;
  } catch { /* lê pela IA */ }
  const sistema = 'Você lê páginas de documentos. Transcreva todo o texto da imagem na ordem de leitura, com as quebras de linha, os números e a pontuação exatamente como aparecem. Não resuma, não traduza e não comente. Se não houver texto, responda com uma linha vazia.';
  const jpgs = await imagensDasPaginas(a, { maxLado: 1600, qualidade: 72 });
  const out: string[] = [];
  for (let i = 0; i < jpgs.length; i++) {
    aoAvancar?.(i, jpgs.length);
    out.push((await perguntar(sistema, 'Transcreva esta página.', [{ mime: 'image/jpeg', base64: paraB64(jpgs[i]) }])).trim());
  }
  return out;
}
export const paraTexto = async (a: PdfArq, av?: (i: number, n: number) => void): Promise<Saida> =>
  ({ nome: a.nome, mime: 'text/plain', bytes: new TextEncoder().encode((await textoPorPagina(a, av)).join('\n\n\f\n\n')), ext: '.txt' });
export const paraWord = async (a: PdfArq, av?: (i: number, n: number) => void): Promise<Saida> =>
  ({ nome: a.nome, mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', bytes: montarDocx(await textoPorPagina(a, av)), ext: '.docx' });
/** OCR: deixa o PDF pesquisável (o texto lido vai escondido por baixo de cada página). */
export const ocr = async (a: PdfArq, av?: (i: number, n: number) => void) => comTextoInvisivel(await bytesDe(a), await textoPorPagina(a, av));

export async function resumirPdf(a: PdfArq, av?: (i: number, n: number) => void) {
  const texto = (await textoPorPagina(a, av)).join('\n\n');
  if (!texto.trim()) throw new Error(t('Não encontrei texto neste PDF.'));
  return (await perguntar(`Resuma o documento enviado em ${idiomaResposta()}, em até 8 linhas curtas: do que se trata, quem são as partes, valores, datas e prazos importantes. Sem markdown.`, texto.slice(0, 80000))).trim();
}
export async function perguntarPdf(a: PdfArq, pergunta: string, av?: (i: number, n: number) => void) {
  const texto = (await textoPorPagina(a, av)).join('\n\n');
  return (await perguntar(`Responda em ${idiomaResposta()}, de forma curta e direta, só com base no documento abaixo. Se a resposta não estiver no documento, diga isso.\n\nDOCUMENTO:\n${texto.slice(0, 80000)}`, pergunta)).trim();
}
/** Sugere um nome claro para o arquivo (ex.: "Contrato de locação - Ana Souza - 2026"). */
export async function sugerirNome(a: PdfArq) {
  const txt = (await textoPorPagina({ ...a, paginas: Math.min(a.paginas || 1, 2) })).join('\n').slice(0, 6000);
  const r = (await perguntar('Sugira um nome de arquivo curto e claro (até 60 caracteres) para o documento abaixo, no formato "Tipo - Quem - Data ou número". Responda só com o nome, sem extensão, sem aspas.', txt)).trim();
  return r.replace(/["'\n\r]/g, '').replace(/\.pdf$/i, '').slice(0, 80);
}

export { temTratamento, diminuir };
